import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import Navbar from "../NavBar";
import NewSidebar from "../newSideBar";
import UserDetails from "./UserDetails";
import InboxPanel from "./InboxPanel";
import EmojiPicker from "emoji-picker-react";
import "./inbox.css";
import { io } from "socket.io-client";
import debounce from "lodash.debounce";

// Constants
const CHAT_STATUS = {
  MY_OPEN: 2,
  SOLVED: 3,
};

const INACTIVITY_THRESHOLD = 5 * 60 * 1000;

// ========== useChatData ==========
const useChatData = (status) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const localURL = localStorage.getItem("shopURL");

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      "x-shop-url": localURL,
    }),
    [localURL]
  );

  const fetchCollections = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/email-list/${status}`,
        { headers }
      );

      const users = res.data.users || [];

      // Fetch unread counts for each user
      const unreadPromises = users.map(async (user) => {
        try {
          const unreadRes = await axios.get(
            `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/unread-count/${user.email}`,
            { headers }
          );
          return { email: user.email, count: unreadRes.data.count || 0 };
        } catch (err) {
          return { email: user.email, count: 0 };
        }
      });
      const unreadResults = await Promise.all(unreadPromises);
      const unreadMap = unreadResults.reduce((acc, item) => {
        acc[item.email] = item.count;
        return acc;
      }, {});

      console.log("unreadMap", unreadMap);

      setUnreadCounts(unreadMap);
      setCollections(users);
    } catch (err) {
      console.error("Error fetching collections:", err);
      setCollections([]);
      setUnreadCounts({});
    } finally {
      setLoading(false);
    }
  }, [status, headers]);

  // Update unread count for specific user
  const updateUnreadCount = useCallback((email, count) => {
    setUnreadCounts((prev) => ({
      ...prev,
      [email]: count,
    }));
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return {
    collections,
    loading,
    unreadCounts,
    updateUnreadCount,
    refetch: fetchCollections,
  };
};

// ========== useSocket ==========
const useSocket = () => {
  const [socket, setSocket] = useState(null);
  const shopUrl = localStorage.getItem("shopURL");

  useEffect(() => {
    const url =
      process.env.REACT_APP_CHAT_BACKEND_SOCKET_URL ||
      process.env.REACT_APP_CHAT_BACKEND_URL;

    const s = io(url, { transports: ["websocket"] });

    s.on("connect", () => {
      console.log("✅ Socket connected:", s.id);
      s.emit("registerShop", shopUrl);
    });

    s.on("registeredShop", (d) => console.log("Shop registered:", d));
    s.on("socketError", (e) => console.error("Socket error:", e));

    setSocket(s);

    return () => s.disconnect();
  }, [shopUrl]);

  return socket;
};

// ========== useChatMessages ==========
const useChatMessages = (
  socket,
  selectedCollection,
  refetchCollections,
  updateUnreadCount,
  scrollToBottomIfNeeded
) => {
  const [messages, setMessages] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false); // <-- NEW

  const refetchThrottled = useMemo(
    () => debounce(refetchCollections, 1000, { leading: true, trailing: true }),
    [refetchCollections]
  );

  // ⭐ Scroll when typing starts
  useEffect(() => {
    if (isTyping) {
      // Small delay to ensure typing indicator is rendered
      setTimeout(() => {
        scrollToBottomIfNeeded();
      }, 100);
    }
  }, [isTyping, scrollToBottomIfNeeded]);

  useEffect(() => {
    if (!socket || !selectedCollection || !socket.connected) return;

    const handleLoadMessages = (payload) => {
      if (payload?.email === selectedCollection) {
        setMessages(payload.messages || []);
        setUserDetails(payload.userInfo || {});
        setMessagesLoading(false);

        // ✅ Mark messages as read when loaded
        socket.emit("markAsRead", {
          email: selectedCollection,
          role: "admin",
        });
        // ⭐ Scroll to bottom after messages load
        setTimeout(() => {
          scrollToBottomIfNeeded();
        }, 100);
      }
    };

    const handleReceiveMessage = (payload) => {
      if (payload?.email === selectedCollection) {
        setMessages((prev) => [
          ...prev,
          {
            text: payload.message,
            admin: payload.admin,
            time: payload.time,
            read: payload.read || false, // ⭐ Include read status
            raw: payload.raw,
          },
        ]);

        // ✅ Auto mark as read when new message received in open chat
        socket.emit("markAsRead", {
          email: selectedCollection,
          role: "admin",
        });
        // ⭐ Scroll to bottom when new message arrives
        setTimeout(() => {
          scrollToBottomIfNeeded();
        }, 100);
      } else {
        refetchThrottled();
      }
    };

    const handleTypingStatus = ({ email, role, isTyping: typing }) => {
      if (email === selectedCollection && role === "user") {
        setIsTyping(typing);
        
        // ⭐ Scroll to bottom when user starts typing
        if (typing) {
          setTimeout(() => {
            scrollToBottomIfNeeded();
          }, 100);
        }
      }
    };

    const handleMessageSeen = (data) => {
      console.log("👁️ Message seen update:", data);
      // Update the specific message's read status
      setMessages((prev) =>
        prev.map((msg) =>
          msg.time === data.time ? { ...msg, read: true } : msg
        )
      );
    };

    const handleAllMessagesSeen = (data) => {
      console.log("✅ All messages read by:", data.role);
      if (data.role === "user") {
        // Mark all user messages as read
        setMessages((prev) =>
          prev.map((msg) => (msg.admin ? { ...msg, read: true } : msg))
        );
      } else if (data.role === "admin") {
        updateUnreadCount(data.email, 0);
      }
    };

    // Load messages
    setMessagesLoading(true);
    socket.emit("joinConversation", { email: selectedCollection });

    socket.on("loadMessages", handleLoadMessages);
    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("typingStatus", handleTypingStatus);
    socket.on("messageSeen", handleMessageSeen);
    socket.on("messagesRead", handleAllMessagesSeen);
    socket.on("newMessagePreview", refetchThrottled);

    const timeoutId = setTimeout(() => setMessagesLoading(false), 10000);

    return () => {
      socket.off("loadMessages", handleLoadMessages);
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("typingStatus", handleTypingStatus);
      socket.off("messageSeen", handleMessageSeen);
      socket.off("messagesRead", handleAllMessagesSeen);
      socket.off("newMessagePreview", refetchThrottled);
      clearTimeout(timeoutId);
    };
  }, [socket?.connected, selectedCollection, refetchThrottled]);

  return {
    messages,
    setMessages,
    userDetails,
    messagesLoading,
    setMessagesLoading,
    isTyping, // <-- NEW return
  };
};

// ========== useScrollManagement ==========
const useScrollManagement = () => {
  const chatContainerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const handleScroll = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    setIsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 10);
  }, []);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: behavior,
      });
      setIsAtBottom(true);
    }
  }, []);

  const scrollToBottomIfNeeded = useCallback(() => {
    if (isAtBottom && chatContainerRef.current) {
      scrollToBottom("smooth");
    }
  }, [isAtBottom, scrollToBottom]);

  return {
    chatContainerRef,
    handleScroll,
    scrollToBottom,
    scrollToBottomIfNeeded,
    isAtBottom,
  };
};

// ========== useTicketSystem ==========
const useTicketSystem = (selectedCollection) => {
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    email: "",
    sender: "Epic-x domain",
    priority: "Low",
    assignee: "Unassigned",
    message: "",
  });

  const localURL = localStorage.getItem("shopURL");

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      "x-shop-url": localURL,
    }),
    [localURL]
  );

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const { subject, email, message } = formData;
    if (!subject || !email || !message) {
      alert("Please fill out all required fields.");
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/create-ticket`,
        formData,
        { headers }
      );
      alert("Ticket Created Successfully!");
      setFormData((p) => ({ ...p, subject: "", message: "" }));
      setShowCreateTicket(false);
    } catch (e) {
      console.error("Ticket creation failed:", e);
      alert("Failed to create ticket. Try again.");
    }
  }, [formData, headers]);

  useEffect(() => {
    if (selectedCollection) {
      setFormData((p) => ({ ...p, email: selectedCollection }));
    }
  }, [selectedCollection]);

  const toggleTicketModal = useCallback(
    () => setShowCreateTicket((p) => !p),
    []
  );

  return {
    showCreateTicket,
    formData,
    handleInputChange,
    handleSubmit,
    toggleTicketModal,
  };
};

// ========== Helpers ==========
const formatDateOnly = (t) => {
  if (!t) return "";
  const [d] = t.split(" ");
  return d;
};

const formatTimeOnly = (t) => {
  if (!t) return "";
  const parts = t.split(" ");
  if (parts.length < 3) return t;
  const time = parts[1].split(":");
  return `${time[0]}:${time[1]} ${parts[2]}`;
};

const isUserActive = (u) => {
  if (!u?.updatedAt) return false;
  return new Date(u.updatedAt).getTime() >= Date.now() - INACTIVITY_THRESHOLD;
};

// ========== Subcomponents ==========
const MyOpenUsersList = ({
  collections,
  selectedEmail,
  onSelect,
  loadingCollection,
  unreadCounts,
}) => {
  const navigate = useNavigate();
  const tenantName = localStorage.getItem("tenantName");

  const handleUserClick = (email) => {
    onSelect(email);
    navigate(`/${tenantName}/chat/Conversation/MyOpen/${email}`);
  };

  if (collections.length === 0)
    return (
      <div className="empty-state">
        <i className="mdi mdi-account-check-outline"></i>
        <p>No open conversations</p>
      </div>
    );

  return collections.map((user) => {
    const unreadCount = unreadCounts[user.email] || 0;
    const isSelected = selectedEmail === user.email;
    const isLoading = loadingCollection === user.email;
    const isActive = isUserActive(user);

    return (
      <div
        key={user.email}
        className={`user-item ${isSelected ? "selected" : ""} ${
          isLoading ? "loading" : ""
        } ${unreadCount > 0 ? "has-unread" : ""}`}
        role="button"
        onClick={() => handleUserClick(user.email)}
      >
        {/* Enhanced Avatar with Status */}
        <div className="user-avatar-container">
          <div className={`user-avatar ${isActive ? "active" : ""}`}>
            {user.email.charAt(0).toUpperCase()}

            {/* Active Status Badge */}
            {isActive && (
              <div className="user-active-indicator">
                <div className="active-pulse"></div>
              </div>
            )}
          </div>

          {/* Loading Spinner */}
          {isLoading && (
            <div className="user-loading-overlay">
              <div className="loading-spinner">
                <div className="spinner-circle"></div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced User Info */}
        <div className="user-info">
          <div className="user-header">
            <div className="user-identity">
              <span className="user-email">{user.email}</span>
              {isActive && (
                <span className="user-active-badge">
                  <span className="active-dot"></span>
                  Active now
                </span>
              )}
            </div>
          </div>

          {/* Enhanced Unread Badge */}
          {unreadCount > 0 && (
            <div className="unread-indicator">
              <span className="unread-count">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            </div>
          )}
        </div>

        <i className="mdi mdi-chevron-right user-arrow"></i>
      </div>
    );
  });
};

const ChatMessages = ({ messages, loading }) => {
  if (loading) {
    return (
      <div className="chat-loading-container">
        <Spinner animation="border" variant="primary" />
        <p>Loading messages...</p>
      </div>
    );
  }

  if (messages.length === 0)
    return (
      <div className="empty-chat-state">
        <i className="mdi mdi-message-text-outline"></i>
        <p>No messages in this conversation</p>
        <small>Start by sending a message below</small>
      </div>
    );

  return messages.map((m, i) => {
    const dateChanged =
      i === 0 ||
      formatDateOnly(m.time) !== formatDateOnly(messages[i - 1]?.time);

    // ⭐ Check if message is read (user messages that admin has seen)
    const isRead = m.read || m.raw?.read || false;

    return (
      <React.Fragment key={i}>
        {dateChanged && (
          <div className="chat-date-divider">
            <span>{formatDateOnly(m.time)}</span>
          </div>
        )}
        <div
          className={`message-container ${m.admin ? "outgoing" : "incoming"}`}
        >
          <div
            className={`message-bubble ${
              m.raw?.likeStatus ? m.raw.likeStatus : ""
            }`}
          >
            <div className="message-content">{m.text}</div>
            <div className="message-meta">
              <span className="message-time">{formatTimeOnly(m.time)}</span>
              {/* ⭐ ADD SEEN STATUS FOR USER MESSAGES (non-admin messages) */}
              {m.admin && (
                <span
                  className={`message-seen-status ${
                    isRead ? "seen" : "delivered"
                  }`}
                >
                  {isRead ? "✓✓" : "✓"}
                </span>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  });
};

const EmojiPickerButton = ({ input, onInputChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  // Click outside to close logic
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [pickerRef]);

  const onEmojiClick = (emojiObject, event) => {
    // Append the selected emoji to the current input
    onInputChange(input + emojiObject.emoji);
    setShowPicker(false); // Close after selection for cleaner UX
  };

  return (
    <div className="emoji-picker-wrapper" ref={pickerRef}>
      <button
        type="button"
        className="emoji-toggle-button"
        onClick={() => setShowPicker((p) => !p)}
        title="Open emoji picker"
      >
        <i className="mdi mdi-emoticon-happy-outline"></i>{" "}
        {/* Icon for emoji */}
      </button>

      {showPicker && (
        <div className="emoji-picker-popup">
          {/* Replace this placeholder with the actual EmojiPicker component */}
          <EmojiPicker onEmojiClick={onEmojiClick} />
          {/* <div style={{ padding: '10px', background: '#fff', border: '1px solid #ccc' }}>
            <p>
              **Emoji Picker Placeholder** <br/>
              Clicking an emoji inserts it into the input.
            </p>
            <button onClick={(e) => { e.stopPropagation(); onEmojiClick({ emoji: "😀" }, e); }}>😀</button>
            <button onClick={(e) => { e.stopPropagation(); onEmojiClick({ emoji: "👍" }, e); }}>👍</button>
          </div> */}
        </div>
      )}
    </div>
  );
};

const MessageInput = ({
  input,
  onInputChange,
  onSendMessage,
  disabled,
  socket,
  selectedCollection,
}) => {
  const typingTimeoutRef = useRef(null);
  const [isTypingLocal, setIsTypingLocal] = useState(false);

  // 🔹 Emit typing status
  const emitTypingStatus = useCallback(
    (status) => {
      if (socket && selectedCollection) {
        socket.emit("typing", {
          email: selectedCollection,
          isTyping: status,
          role: "admin",
        });
      }
    },
    [socket, selectedCollection]
  );

  // 🔹 Handle text change
  const handleChange = (value) => {
    onInputChange(value);

    if (!isTypingLocal) {
      setIsTypingLocal(true);
      emitTypingStatus(true); // user started typing
    }

    // Clear previous timer
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timer to mark stop typing after 2s
    typingTimeoutRef.current = setTimeout(() => {
      setIsTypingLocal(false);
      emitTypingStatus(false);
    }, 2000);
  };

  // 🔹 Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !disabled) {
        onSendMessage();
        setIsTypingLocal(false);
        emitTypingStatus(false);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  return (
    <div className="message-input-container">
      <div className="message-input-group">
        <EmojiPickerButton input={input} onInputChange={handleChange} />
        <input
          type="text"
          placeholder="Type your message..."
          className="message-input"
          value={input}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <button
          className="chat-send-button"
          onClick={onSendMessage}
          disabled={!input.trim() || disabled}
        >
          <i className="mdi mdi-send"></i>
        </button>
      </div>
    </div>
  );
};

const TicketModal = ({ show, formData, onClose, onInputChange, onSubmit }) => {
  if (!show) return null;

  return (
    <div className="epic-ticket-overlay">
      <div className="epic-ticket-modal">
        <button className="epic-ticket-close" onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="epic-ticket-header">
          <h2>Create a Ticket</h2>
        </div>

        <div className="epic-ticket-body">
          <div className="epic-form-group">
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={onInputChange}
              required
            />
            <label>Ticket Subject</label>
          </div>

          <div className="epic-form-row">
            <div className="epic-form-group">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={onInputChange}
                required
              />
              <label>Customer Email</label>
            </div>

            <div className="epic-form-group">
              <select
                name="sender"
                value={formData.sender}
                onChange={onInputChange}
              >
                <option value="Epic-x domain">Epic-x domain</option>
              </select>
              <label>From (sender address)</label>
            </div>
          </div>

          <div className="epic-form-row">
            <div className="epic-form-group">
              <select
                name="priority"
                value={formData.priority}
                onChange={onInputChange}
              >
                <option value="Low">Low</option>
                <option value="Normal">Normal</option>
                <option value="Urgent">Urgent</option>
              </select>
              <label>Priority</label>
            </div>

            <div className="epic-form-group">
              <select
                name="assignee"
                value={formData.assignee}
                onChange={onInputChange}
              >
                <option value="Unassigned">Unassigned</option>
                <option value="Admin name">Admin name</option>
              </select>
              <label>Assignee</label>
            </div>
          </div>

          <div className="epic-form-group epic-message-group">
            <textarea
              name="message"
              value={formData.message}
              onChange={onInputChange}
              placeholder="Enter the ticket message..."
              rows="5"
            ></textarea>
          </div>
        </div>

        <div className="epic-ticket-footer">
          <button className="epic-submit-btn" onClick={onSubmit}>
            Submit Ticket
          </button>
        </div>
      </div>
    </div>
  );
};

const TypingIndicator = () => (
  <div className="typing-indicator">
    <div className="typing-dots">
      <span></span>
      <span></span>
      <span></span>
    </div>
    <span>User is typing...</span>
  </div>
);

// ========== Main Component ==========
function MyopenNew() {
  const navigate = useNavigate();
  const { collection } = useParams();
  const tenantName = localStorage.getItem("tenantName");

  const [selectedCollection, setSelectedCollection] = useState("");
  const [input, setInput] = useState("");
  const [loadingCollection, setLoadingCollection] = useState("");

  const { collections, loading, unreadCounts, updateUnreadCount, refetch } =
    useChatData(CHAT_STATUS.MY_OPEN);
  const socket = useSocket();
  
  const { 
    chatContainerRef, 
    handleScroll, 
    scrollToBottomIfNeeded,
    isAtBottom 
  } = useScrollManagement();
  const {
    messages,
    setMessages,
    userDetails,
    messagesLoading,
    setMessagesLoading,
    isTyping,
  } = useChatMessages(socket, selectedCollection, refetch, updateUnreadCount,scrollToBottomIfNeeded);
  const {
    showCreateTicket,
    formData,
    handleInputChange,
    handleSubmit,
    toggleTicketModal,
  } = useTicketSystem(selectedCollection);

  const handleSelectCollection = useCallback(
    (email) => {
      setSelectedCollection((prev) => {
        if (prev !== email) {
          setMessages([]); // clear only if different collection
          setMessagesLoading(true);
          setLoadingCollection(email);
        }
        return email;
      });
    },
    [setMessages, setMessagesLoading]
  );

  function updateCurrentTime() {
    const currentTime = new Date();
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const seconds = currentTime.getSeconds();
    const milliseconds = currentTime.getMilliseconds();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
    const formattedMilliseconds =
      milliseconds < 10
        ? `00${milliseconds}`
        : milliseconds < 100
        ? `0${milliseconds}`
        : milliseconds;
    const month = (currentTime.getMonth() + 1).toString().padStart(2, "0");
    const day = currentTime.getDate().toString().padStart(2, "0");
    const year = currentTime.getFullYear();
    return `${day}-${month}-${year} ${formattedHours}:${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds} ${ampm}`;
  }

  const handleSendMessage = useCallback(() => {
    if (!input.trim() || !selectedCollection || !socket) return;

    const time = updateCurrentTime();
    const payload = {
      email: selectedCollection,
      message: input,
      time: time,
      chatMode: "manual",
      feedbackButtons: false,
    };
    socket.emit("adminSendMessage", payload);
    setInput("");
  }, [input, selectedCollection, socket, setMessages]);

  const handleStatusChange = useCallback(async () => {
    if (!selectedCollection) return;
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-shop-url": localStorage.getItem("shopURL"),
      };
      await axios.post(
        `${process.env.REACT_APP_CHAT_BACKEND_URL}/api/admin/status-update`,
        { email: selectedCollection, status: CHAT_STATUS.SOLVED },
        { headers }
      );
      navigate(`/${tenantName}/chat/Conversation/Solved/${selectedCollection}`);
    } catch (e) {
      console.error("Status update failed:", e);
    }
  }, [selectedCollection, tenantName, navigate]);

  // Initial select from URL
  useEffect(() => {
    if (collection && collection !== ":collection") {
      setSelectedCollection(collection);
      setMessages([]);
      setMessagesLoading(true);
      setLoadingCollection(collection);
    }
  }, [collection]); // run when collection changes

  // Clear loading state when messages are loaded
  useEffect(() => {
    if (!messagesLoading && loadingCollection) {
      setLoadingCollection("");
    }
  }, [messagesLoading, loadingCollection]);

  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages, scrollToBottomIfNeeded]);

  if (loading) {
    return (
      <div className="d-flex flex-column w-100">
        <Navbar />
        <div className="d-flex w-100">
          <NewSidebar />
          <div
            className="main-panel"
            style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
          >
            <div className="loading-container">
              <Spinner animation="border" variant="primary" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column w-100">
      <Navbar />
      <div className="d-flex w-100">
        <NewSidebar />
        <div
          className="main-panel"
          style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }}
        >
          <div className="content-wrapper">
            <div className="d-flex justify-content-between">
              <h2 className="text-dark font-weight-bold mb-2">Conversation</h2>
              <button
                className="btn"
                style={{ background: "#47c856", color: "white" }}
                onClick={handleStatusChange}
              >
                Mark As Solved
              </button>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="tab-content tab-transparent-content">
                  <div className="tab-pane fade show active">
                    <div className="row">
                      <div className="col-12 grid-margin">
                        <div className="card">
                          <div className="card-body">
                            <div className="row d-flex flex-wrap">
                              <InboxPanel />

                              {/* Left User List */}
                              <div className="myopen-users-panel col-md-3">
                                <div className="panel-header">
                                  <h3 className="panel-title">
                                    My Open Conversations
                                  </h3>
                                  <div className="panel-badge">
                                    {collections.length}
                                  </div>
                                </div>
                                <div className="users-list-container">
                                  <MyOpenUsersList
                                    collections={collections}
                                    onSelect={handleSelectCollection}
                                    selectedEmail={selectedCollection}
                                    loadingCollection={loadingCollection}
                                    unreadCounts={unreadCounts}
                                  />
                                </div>
                              </div>

                              {/* Chat Section */}
                              <div className="myopen-chat-panel col-md-4">
                                <div className="chat-panel-header">
                                  <h3 className="chat-title">
                                    {selectedCollection
                                      ? `Conversation`
                                      : "Select a conversation"}
                                  </h3>
                                </div>
                                <div
                                  className={`chat-messages-container ${
                                    messagesLoading ? "loading" : ""
                                  }`}
                                  ref={chatContainerRef}
                                  onScroll={handleScroll}
                                >
                                  <ChatMessages
                                    messages={messages}
                                    loading={messagesLoading}
                                  />

                                  {isTyping && <TypingIndicator />}

                                </div>
                                <MessageInput
                                  input={input}
                                  onInputChange={setInput}
                                  onSendMessage={handleSendMessage}
                                  disabled={
                                    messagesLoading || !selectedCollection
                                  }
                                  socket={socket}
                                  selectedCollection={selectedCollection}
                                />
                              </div>

                              {/* Right Info Panel */}
                              <div className="col-md-3">
                                <h4 className="card-title mb-0">Information</h4>
                                <div className="info-panel">
                                  <UserDetails {...userDetails} />
                                  <button
                                    className="btn btn-primary w-100 mt-2"
                                    onClick={toggleTicketModal}
                                    disabled={
                                      messagesLoading || !selectedCollection
                                    }
                                  >
                                    Add a Ticket
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <TicketModal
              show={showCreateTicket}
              formData={formData}
              onClose={toggleTicketModal}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyopenNew;
